import React from 'react';
import { PublicLayout } from '@/components/PublicLayout';
import { useLanguage } from '@/contexts/LanguageContext';
import { Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: 'Politique de confidentialité',
      lastUpdate: 'Dernière mise à jour : Janvier 2026',
      intro: 'AILE (Association d\'Intégration Lyonnaise pour Étudiants) s\'engage à protéger la vie privée des utilisateurs de son site web. Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos données personnelles.',
      sections: [
        {
          icon: Database,
          title: '1. Données collectées',
          content: `Nous collectons les données suivantes :
• Données d'identification : nom, prénom, adresse email, numéro de téléphone
• Données de navigation : adresse IP, type de navigateur, pages visitées
• Données de contact : messages envoyés via le formulaire de contact
• Données relatives aux demandes de logement : critères de recherche, préférences

Ces données sont collectées lorsque vous :
• Remplissez le formulaire de contact
• Consultez les annonces de logement
• Vous inscrivez à notre newsletter`
        },
        {
          icon: Eye,
          title: '2. Utilisation des données',
          content: `Vos données personnelles sont utilisées pour :
• Répondre à vos demandes de renseignements
• Vous accompagner dans votre recherche de logement
• Vous informer des actualités et événements de l'association
• Améliorer nos services et notre site web
• Établir des statistiques de fréquentation anonymes

Nous ne vendons ni ne louons vos données personnelles à des tiers.`
        },
        {
          icon: Lock,
          title: '3. Protection des données',
          content: `Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données :
• Chiffrement des données sensibles
• Accès restreint aux données personnelles
• Serveurs sécurisés
• Mises à jour régulières de sécurité

En cas de violation de données, nous nous engageons à vous informer dans les 72 heures conformément au RGPD.`
        },
        {
          icon: UserCheck,
          title: '4. Vos droits',
          content: `Conformément au RGPD, vous disposez des droits suivants :
• Droit d'accès : obtenir une copie de vos données personnelles
• Droit de rectification : corriger des données inexactes
• Droit à l'effacement : demander la suppression de vos données
• Droit à la portabilité : recevoir vos données dans un format structuré
• Droit d'opposition : vous opposer au traitement de vos données
• Droit de limitation : restreindre le traitement de vos données

Pour exercer ces droits, contactez-nous à : contact@aile-france.fr`
        },
        {
          icon: Shield,
          title: '5. Cookies',
          content: `Notre site utilise des cookies pour :
• Assurer le bon fonctionnement du site
• Mémoriser vos préférences de langue
• Analyser la fréquentation du site (cookies analytiques)

Vous pouvez configurer votre navigateur pour refuser les cookies. Cependant, certaines fonctionnalités du site pourraient ne plus être disponibles.`
        },
        {
          icon: Mail,
          title: '6. Contact',
          content: `Pour toute question relative à cette politique de confidentialité ou à vos données personnelles, vous pouvez nous contacter :

AILE - Aide à l'Intégration et au Logement des étudiants
1 place Abbé Pierre, 69009 Lyon
Email : contact@aile-france.fr
Téléphone : +33 6 81 65 25 38`
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdate: 'Last updated: January 2026',
      intro: 'AILE (Lyon Integration Association for Students) is committed to protecting the privacy of users of its website. This privacy policy explains how we collect, use and protect your personal data.',
      sections: [
        {
          icon: Database,
          title: '1. Data collected',
          content: `We collect the following data:
• Identification data: surname, first name, email address, phone number
• Browsing data: IP address, browser type, pages visited
• Contact data: messages sent via the contact form
• Housing request data: search criteria, preferences

This data is collected when you:
• Fill out the contact form
• View housing listings
• Subscribe to our newsletter`
        },
        {
          icon: Eye,
          title: '2. Use of data',
          content: `Your personal data is used to:
• Respond to your inquiries
• Assist you in your housing search
• Inform you about news and events of the association
• Improve our services and website
• Compile anonymous attendance statistics

We do not sell or rent your personal data to third parties.`
        },
        {
          icon: Lock,
          title: '3. Data protection',
          content: `We implement appropriate security measures to protect your data:
• Encryption of sensitive data
• Restricted access to personal data
• Secure servers
• Regular security updates

In case of data breach, we commit to informing you within 72 hours in accordance with GDPR.`
        },
        {
          icon: UserCheck,
          title: '4. Your rights',
          content: `Under GDPR, you have the following rights:
• Right of access: obtain a copy of your personal data
• Right of rectification: correct inaccurate data
• Right to erasure: request deletion of your data
• Right to portability: receive your data in a structured format
• Right to object: object to the processing of your data
• Right to restriction: restrict the processing of your data

To exercise these rights, contact us at: contact@aile-france.fr`
        },
        {
          icon: Shield,
          title: '5. Cookies',
          content: `Our site uses cookies to:
• Ensure proper functioning of the site
• Remember your language preferences
• Analyze site traffic (analytical cookies)

You can configure your browser to refuse cookies. However, some features of the site may no longer be available.`
        },
        {
          icon: Mail,
          title: '6. Contact',
          content: `For any questions regarding this privacy policy or your personal data, you can contact us:

AILE - Lyon Integration Association for Students
1 place Abbé Pierre, 69009 Lyon
Email: contact@aile-france.fr
Phone: +33 6 81 65 25 38`
        }
      ]
    }
  };

  const t = content[language];

  return (
    <PublicLayout>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#127abb]/10 rounded-2xl mb-6">
              <Shield className="text-[#127abb]" size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {t.title}
            </h1>
            <p className="text-slate-500">{t.lastUpdate}</p>
          </div>

          {/* Introduction */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-12">
            <p className="text-slate-700 leading-relaxed">{t.intro}</p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <div 
                key={index} 
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#127abb]/10 rounded-xl flex items-center justify-center">
                    <section.icon className="text-[#127abb]" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-3">
                      {section.title}
                    </h2>
                    <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default PrivacyPolicy;
